import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import ShareWhatsApp from "@/components/ShareWhatsApp";
import PayMaintenanceModal from "@/components/PayMaintenanceModal";
import MaintenanceReceiptModal from "@/components/MaintenanceReceiptModal";
import { Receipt, Building2, CheckCircle2, ShieldCheck, ArrowRight, FileCheck, Upload } from "lucide-react";

export default function Bills() {
    const { user } = useAuth();
    const [bills, setBills] = useState([]);
    const isAdmin = user?.role === "admin";
    const [form, setForm] = useState({ tower: "", flat_no: "", amount: "", period: "", due_date: "" });
    const [payingBill, setPayingBill] = useState(null);
    const [viewingReceipt, setViewingReceipt] = useState(null);

    const load = async () => {
        try { const r = await api.get(isAdmin ? "/bills" : "/bills/me"); setBills(r.data); } catch {}
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

    const create = async (e) => {
        e.preventDefault();
        try {
            const r = await api.post("/bills", { ...form, amount: parseFloat(form.amount) });
            setBills((arr) => [r.data, ...arr]);
            setForm({ tower: "", flat_no: "", amount: "", period: "", due_date: "" });
            toast.success("Bill created");
        } catch { toast.error("Failed"); }
    };

    const pay = async (b) => {
        try {
            const r = await api.post(`/bills/${b.bill_id}/pay`);
            setBills((arr) => arr.map((x) => x.bill_id === b.bill_id ? r.data : x));
            toast.success("Marked as paid");
        } catch { toast.error("Failed"); }
    };

    const handlePaymentSuccess = (updatedBill) => {
        setBills((arr) => arr.map((x) => x.bill_id === updatedBill.bill_id ? updatedBill : x));
        setPayingBill(null);
        setViewingReceipt(updatedBill);
    };

    const total = bills.filter((b) => b.status === "unpaid").reduce((s, b) => s + b.amount, 0);

    return (
        <div className="animate-fade-up" data-testid="bills-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 sm:gap-4">
                <div>
                    <p className="nest-stat-label">Maintenance</p>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-1 sm:mt-2">{isAdmin ? "Society dues" : "Your bills"}</h1>
                    <p className="text-nest-ink2 text-sm sm:text-base mt-1 sm:mt-2">{isAdmin ? "Issue bills to flats, monitor collection." : `Unpaid total · ₹${total.toFixed(2)}`}</p>
                </div>
            </div>

            {/* Society QR & Official Info Callout Banner */}
            <div className="mt-6 rounded-2xl bg-gradient-to-r from-amber-500/10 via-amber-600/5 to-transparent border border-amber-500/20 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3.5">
                    <div className="p-2.5 rounded-xl bg-amber-500/15 text-amber-900 border border-amber-500/30 shrink-0">
                        <Building2 size={22} className="text-amber-700" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-display font-bold text-nest-ink text-sm sm:text-base">
                                Pyramid City 3 Owners Co-Operative Housing Society Maryadit
                            </h3>
                            <span className="text-[10px] font-mono font-bold bg-amber-100 text-amber-900 px-2 py-0.5 rounded-full border border-amber-300">
                                Regn: NGP/ARN/HCG(TC)/1709/2021
                            </span>
                        </div>
                        <p className="text-xs text-nest-ink3 mt-1">
                            ICICI Bank UPI: <span className="font-mono font-bold text-nest-ink select-all">eazypay.2000015066@icici</span> · Besa-Pipla Road, Nagpur
                        </p>
                    </div>
                </div>

                {bills.some((b) => b.status === "unpaid") && (
                    <button
                        onClick={() => {
                            const firstUnpaid = bills.find((b) => b.status === "unpaid");
                            if (firstUnpaid) setPayingBill(firstUnpaid);
                        }}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-nest-ink hover:bg-nest-clay text-white text-xs font-bold transition shadow-xs cursor-pointer shrink-0"
                    >
                        <Upload size={15} />
                        <span>Upload Screenshot & Get Receipt</span>
                        <ArrowRight size={14} />
                    </button>
                )}
            </div>

            <div className="grid lg:grid-cols-3 gap-6 sm:gap-8 mt-6 sm:mt-10">
                {isAdmin && (
                    <form onSubmit={create} className="nest-card p-6 self-start" data-testid="bill-form">
                        <p className="nest-stat-label">Create bill</p>
                        <h3 className="font-display text-xl mt-1 tracking-tight">Issue a new dues entry</h3>
                        <div className="mt-5 grid gap-4">
                            <div className="grid grid-cols-2 gap-3">
                                <input required value={form.tower} onChange={(e) => setForm({ ...form, tower: e.target.value })} placeholder="Tower" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-tower-input"/>
                                <input required value={form.flat_no} onChange={(e) => setForm({ ...form, flat_no: e.target.value })} placeholder="Flat no." className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-flat-input"/>
                            </div>
                            <input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount (₹)" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-amount-input"/>
                            <input required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="Period (e.g., Nov-26)" className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-period-input"/>
                            <input required type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-2" data-testid="bill-duedate-input"/>
                            <button type="submit" className="nest-btn-primary" data-testid="bill-submit-btn">Create bill</button>
                        </div>
                    </form>
                )}
                <div className={`${isAdmin ? "lg:col-span-2" : "lg:col-span-3"} nest-card overflow-hidden`}>
                    <div className="hidden md:grid grid-cols-12 gap-3 p-4 bg-nest-alt nest-stat-label border-b border-nest-line">
                        <div className="col-span-2">Flat</div>
                        <div className="col-span-2">Period</div>
                        <div className="col-span-2">Amount</div>
                        <div className="col-span-2">Due / Paid</div>
                        <div className="col-span-4 text-right">Status & Action</div>
                    </div>
                    {bills.length === 0 && <div className="p-10 text-center text-nest-ink2" data-testid="no-bills">No bills yet.</div>}
                    {bills.map((b) => (
                        <div key={b.bill_id} className="border-b border-nest-line hover:bg-nest-alt/25 transition" data-testid={`bill-${b.bill_id}`}>
                            {/* Mobile Card View (< md) */}
                            <div className="md:hidden p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-sm text-nest-ink bg-nest-alt px-2.5 py-1 rounded-lg border border-nest-line">
                                            {b.tower}-{b.flat_no}
                                        </span>
                                        <span className="text-xs text-nest-ink2 font-medium">{b.period}</span>
                                    </div>
                                    {b.status === "paid" ? (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-nest-sage text-white" data-testid={`bill-paid-${b.bill_id}`}>Paid</span>
                                    ) : (
                                        <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-amber-500 text-white">Unpaid</span>
                                    )}
                                </div>
                                <div className="flex items-baseline justify-between pt-1">
                                    <div>
                                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">
                                            {b.status === "paid" ? "Paid on" : "Due by"}
                                        </span>
                                        <span className="text-xs text-nest-ink2">
                                            {b.status === "paid" && b.paid_at ? new Date(b.paid_at).toLocaleDateString("en-IN") : b.due_date}
                                        </span>
                                        {b.status === "paid" && b.utr_no && (
                                            <span className="block font-mono text-[10px] text-nest-clay truncate max-w-[150px]">
                                                UTR: {b.utr_no}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <span className="text-[10px] uppercase font-bold text-nest-ink3 block">Total Amount</span>
                                        <span className="font-display text-xl font-bold text-nest-ink">₹{b.amount.toFixed(2)}</span>
                                    </div>
                                </div>

                                {b.status !== "paid" ? (
                                    <div className="flex flex-col gap-2 pt-2 border-t border-nest-line/60">
                                        <button
                                            onClick={() => setPayingBill(b)}
                                            className="w-full text-xs py-2.5 rounded-xl bg-nest-ink text-white font-semibold flex items-center justify-center gap-2 hover:bg-nest-clay transition cursor-pointer shadow-xs"
                                            data-testid={`bill-pay-${b.bill_id}`}
                                        >
                                            <Upload size={15} />
                                            <span>Upload Screenshot & Get Receipt</span>
                                        </button>
                                        <div className="flex items-center gap-2">
                                            {isAdmin && (
                                                <button
                                                    onClick={() => pay(b)}
                                                    className="flex-1 text-xs py-2 rounded-xl border border-nest-line bg-nest-alt hover:bg-nest-line text-nest-ink font-semibold transition"
                                                    title="Quick mark paid without UTR"
                                                >
                                                    Quick Mark Paid
                                                </button>
                                            )}
                                            <div className="flex-1">
                                                <ShareWhatsApp
                                                    testid={`bill-share-${b.bill_id}`}
                                                    label="Remind on WhatsApp"
                                                    text={`🧾 Maintenance reminder for flat *${b.tower}-${b.flat_no}*\nPeriod: ${b.period}\nAmount: ₹${b.amount.toFixed(2)}\nDue: ${b.due_date}\nSociety UPI: eazypay.2000015066@icici (Pyramid City 3)\n— NeighborNest`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pt-2 border-t border-nest-line/60">
                                        <button
                                            onClick={() => setViewingReceipt(b)}
                                            className="w-full text-xs py-2.5 rounded-xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 text-emerald-900 font-bold flex items-center justify-center gap-1.5 transition cursor-pointer shadow-2xs"
                                            data-testid={`bill-receipt-${b.bill_id}`}
                                        >
                                            <Receipt size={14} className="text-emerald-700" />
                                            <span>View Receipt #{b.receipt_no || b.receipt_no_int || "5378"}</span>
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Desktop / Laptop Table Row (>= md) */}
                            <div className="hidden md:grid grid-cols-12 gap-3 p-4 items-center">
                                <div className="col-span-2 font-semibold text-nest-ink">
                                    <span>{b.tower}-{b.flat_no}</span>
                                    {b.paid_by_name && (
                                        <p className="text-[11px] text-nest-ink3 truncate font-normal">{b.paid_by_name}</p>
                                    )}
                                </div>
                                <div className="col-span-2 text-sm text-nest-ink2">{b.period}</div>
                                <div className="col-span-2 font-display text-base font-bold text-nest-ink">₹{b.amount.toFixed(2)}</div>
                                <div className="col-span-2 text-xs text-nest-ink2">
                                    {b.status === "paid" ? (
                                        <div>
                                            <span className="text-[10px] text-nest-ink3 uppercase font-bold block">Paid On</span>
                                            <span className="font-medium text-nest-ink">{b.paid_at ? new Date(b.paid_at).toLocaleDateString("en-IN") : "Done"}</span>
                                            {b.utr_no && <span className="block font-mono text-[10px] text-nest-clay truncate" title={`UTR: ${b.utr_no}`}>UTR: {b.utr_no}</span>}
                                        </div>
                                    ) : (
                                        <div>
                                            <span className="text-[10px] text-nest-ink3 uppercase font-bold block">Due Date</span>
                                            <span>{b.due_date}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="col-span-4 flex justify-end items-center gap-2 flex-wrap">
                                    {b.status === "paid" ? (
                                        <>
                                            <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded bg-nest-sage text-white" data-testid={`bill-paid-${b.bill_id}`}>Paid</span>
                                            <button
                                                onClick={() => setViewingReceipt(b)}
                                                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold transition cursor-pointer shadow-2xs"
                                                data-testid={`bill-receipt-${b.bill_id}`}
                                                title="View & Download Official Maintenance Receipt"
                                            >
                                                <Receipt size={13} className="text-emerald-700" />
                                                <span>Receipt #{b.receipt_no || b.receipt_no_int || "5378"}</span>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                onClick={() => setPayingBill(b)}
                                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-nest-ink text-white hover:bg-nest-clay transition cursor-pointer font-semibold shadow-2xs"
                                                data-testid={`bill-pay-${b.bill_id}`}
                                            >
                                                <Upload size={13} />
                                                <span>Upload Screenshot & Get Receipt</span>
                                            </button>
                                            {isAdmin && (
                                                <button
                                                    onClick={() => pay(b)}
                                                    className="text-xs px-2.5 py-1.5 rounded-lg border border-nest-line hover:bg-nest-alt text-nest-ink2 hover:text-nest-ink transition cursor-pointer"
                                                    title="Quick mark as paid without UTR"
                                                >
                                                    Mark Paid
                                                </button>
                                            )}
                                            <ShareWhatsApp
                                                testid={`bill-share-${b.bill_id}`}
                                                label="Remind"
                                                text={`🧾 Maintenance reminder for flat *${b.tower}-${b.flat_no}*\nPeriod: ${b.period}\nAmount: ₹${b.amount.toFixed(2)}\nDue: ${b.due_date}\nUPI ID: eazypay.2000015066@icici (Pyramid City 3)\n— NeighborNest`}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pay Maintenance Modal */}
            {payingBill && (
                <PayMaintenanceModal
                    bill={payingBill}
                    user={user}
                    onClose={() => setPayingBill(null)}
                    onSuccess={handlePaymentSuccess}
                />
            )}

            {/* Official Maintenance Receipt Modal */}
            {viewingReceipt && (
                <MaintenanceReceiptModal
                    bill={viewingReceipt}
                    onClose={() => setViewingReceipt(null)}
                />
            )}
        </div>
    );
}
